import { Configuration, FrontendApi } from "@ory/client";
import type {
  LoginFlow,
  RegistrationFlow,
  RecoveryFlow,
  VerificationFlow,
  UpdateLoginFlowBody,
  UpdateRegistrationFlowBody,
  Session,
} from "@ory/client";
import { logger } from "~/utils/logger";

export const useKratos = () => {
  const config = useRuntimeConfig();
  // Use generic auth API endpoint to hide implementation details
  const kratosUrl = config.public.kratosUrl || "/auth-api";

  const kratos = new FrontendApi(
    new Configuration({
      basePath: kratosUrl,
      baseOptions: {
        withCredentials: true,
      },
    }),
  );

  /**
   * Initialize login flow
   */
  const initLoginFlow = async (refresh = false, returnTo?: string) => {
    try {
      const { data: flow } = await kratos.createBrowserLoginFlow({
        refresh,
        returnTo,
      });
      return flow;
    } catch (error) {
      logger.error("Failed to initialize login flow:", error);
      throw error;
    }
  };

  /**
   * Submit login flow
   */
  const submitLoginFlow = async (
    flowId: string,
    values: UpdateLoginFlowBody,
  ) => {
    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flowId,
        updateLoginFlowBody: values,
      });
      return data;
    } catch (error: any) {
      logger.error("Login submission failed:", error);
      throw error;
    }
  };

  /**
   * Initialize registration flow
   */
  const initRegistrationFlow = async (returnTo?: string) => {
    try {
      const { data: flow } = await kratos.createBrowserRegistrationFlow({
        returnTo,
      });
      return flow;
    } catch (error) {
      logger.error("Failed to initialize registration flow:", error);
      throw error;
    }
  };

  /**
   * Submit registration flow
   */
  const submitRegistrationFlow = async (
    flowId: string,
    values: UpdateRegistrationFlowBody,
  ) => {
    try {
      const { data } = await kratos.updateRegistrationFlow({
        flow: flowId,
        updateRegistrationFlowBody: values,
      });
      return data;
    } catch (error: any) {
      logger.error("Registration submission failed:", error);
      throw error;
    }
  };

  /**
   * Initialize recovery flow (password reset)
   */
  const initRecoveryFlow = async () => {
    try {
      const { data: flow } = await kratos.createBrowserRecoveryFlow();
      return flow;
    } catch (error) {
      logger.error("Failed to initialize recovery flow:", error);
      throw error;
    }
  };

  /**
   * Submit recovery flow (step 1: request code by email)
   */
  const submitRecoveryFlow = async (
    flowId: string,
    email: string,
    csrfToken?: string,
  ) => {
    try {
      const { data } = await kratos.updateRecoveryFlow({
        flow: flowId,
        updateRecoveryFlowBody: {
          csrf_token: csrfToken,
          method: "code",
          email,
        },
      });
      return data;
    } catch (error: any) {
      logger.error("Recovery submission failed:", error);
      throw error;
    }
  };

  /**
   * Get existing recovery flow
   */
  const getRecoveryFlow = async (flowId: string) => {
    try {
      const { data: flow } = await kratos.getRecoveryFlow({ id: flowId });
      return flow;
    } catch (error) {
      logger.error("Failed to get recovery flow:", error);
      throw error;
    }
  };

  /**
   * Submit recovery code (step 2: verify code and set new password)
   * Requires both code AND email (just like verification flow)
   */
  /**
   * Submit recovery code (step 2 of recovery - verify the code)
   * Only send the code - sending email causes Kratos to send a new code!
   */
  const submitRecoveryCodeFlow = async (
    flowId: string,
    code: string,
    csrfToken?: string,
  ) => {
    try {
      const { data } = await kratos.updateRecoveryFlow({
        flow: flowId,
        updateRecoveryFlowBody: {
          csrf_token: csrfToken,
          method: "code",
          code, // ONLY code - no email!
        },
      });
      return data;
    } catch (error: any) {
      logger.error("Recovery code submission failed:", error);
      throw error;
    }
  };

  /**
   * Initialize verification flow (email verification)
   */
  const initVerificationFlow = async () => {
    try {
      const { data: flow } = await kratos.createBrowserVerificationFlow();
      return flow;
    } catch (error) {
      logger.error("Failed to initialize verification flow:", error);
      throw error;
    }
  };

  /**
   * Get existing verification flow
   */
  const getVerificationFlow = async (flowId: string) => {
    try {
      const { data: flow } = await kratos.getVerificationFlow({ id: flowId });
      return flow;
    } catch (error) {
      logger.error("Failed to get verification flow:", error);
      throw error;
    }
  };

  /**
   * Submit verification flow
   * For code method, after email is sent, we need to submit both email and code
   */
  const submitVerificationFlow = async (
    flowId: string,
    code: string,
    email: string,
    csrfToken?: string,
  ) => {
    try {
      logger.log("Submitting verification with:", {
        flowId,
        codeLength: code.length,
        email,
        hasCsrf: !!csrfToken,
      });

      const { data } = await kratos.updateVerificationFlow({
        flow: flowId,
        updateVerificationFlowBody: {
          csrf_token: csrfToken,
          method: "code",
          code: code,
          email: email, // Include email to specify which address we're verifying
        },
      });

      logger.log("Verification response state:", data.state);
      logger.log("Verification response:", data);
      return data;
    } catch (error: any) {
      logger.error("Verification submission failed:", error);
      logger.error("Error details:", error.response?.data);
      throw error;
    }
  };

  /**
   * Request verification email (sends the code to user's email)
   * This is step 1: request the code to be sent
   */
  const requestVerificationEmail = async (
    flowId: string,
    email: string,
    csrfToken?: string,
  ) => {
    try {
      const callId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const isPopup = sessionStorage.getItem("is_oauth_popup") === "true";
      const windowInfo = {
        isPopup,
        windowName: window.name,
        hasOpener: !!window.opener,
        url: window.location.href,
        referrer: document.referrer,
      };

      logger.log("📧📧📧 ============ EMAIL SEND REQUEST ============");
      logger.log("📧 SENDING VERIFICATION EMAIL TO:", email);
      logger.log("📧 Flow ID:", flowId);
      logger.log("📧 Call ID:", callId);
      logger.log("📧 Window Info:", windowInfo);
      logger.log("📧 Stack trace:", new Error().stack);
      logger.log("📧 =============================================");

      const { data } = await kratos.updateVerificationFlow({
        flow: flowId,
        updateVerificationFlowBody: {
          csrf_token: csrfToken,
          method: "code",
          email: email,
        },
      });

      logger.log("✅ Verification email SENT successfully for call:", callId);
      return data;
    } catch (error: any) {
      logger.error("Failed to request verification email:", error);
      throw error;
    }
  };

  /**
   * Get current session
   */
  const getSession = async (): Promise<Session | null> => {
    try {
      const { data: session } = await kratos.toSession();
      return session;
    } catch (error: any) {
      // 401 errors are expected when not logged in, don't log
      if (error.response?.status === 401) {
        return null;
      }
      // For other errors, log them
      logger.error("Session check failed:", error);
      return null;
    }
  };

  /**
   * Logout user - Clear session without page reload
   */
  const logout = async () => {
    try {
      // Get logout URL from Kratos
      const { data } = await kratos.createBrowserLogoutFlow();

      if (data.logout_url) {
        logger.log("🔓 Calling Kratos logout URL to clear session cookie");
        // Call logout URL via fetch to clear cookies without page reload
        // Use redirect: 'manual' to prevent CORS errors from following the redirect
        await fetch(data.logout_url, {
          method: "GET",
          credentials: "include", // Important: include cookies
          redirect: "manual", // Don't follow redirect to avoid CORS
        });

        logger.log("✅ Logout successful");
      } else {
        logger.error("❌ No logout URL provided by Kratos");
      }
    } catch (error) {
      // Ignore fetch errors - the logout token is consumed regardless
      logger.log("🔓 Logout token consumed (ignoring redirect error)");
    }
  };

  /**
   * Submit OAuth flow (Google, etc.)
   */
  const submitOAuthFlow = async (
    flowId: string,
    provider: string,
    csrfToken: string,
    flowType: "login" | "registration",
    upstreamParams?: Record<string, string>,
  ) => {
    try {
      const endpoint =
        flowType === "login"
          ? `/self-service/login?flow=${flowId}`
          : `/self-service/registration?flow=${flowId}`;

      // Build the body with upstream parameters if provided
      const body: any = {
        csrf_token: csrfToken,
        provider: provider,
      };

      // Add upstream parameters with the correct prefix
      if (upstreamParams) {
        Object.entries(upstreamParams).forEach(([key, value]) => {
          body[`upstream_parameters.${key}`] = value;
        });
      }

      const response = await fetch(`${kratosUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });

      // Kratos will return a redirect or the flow with redirect info
      if (response.redirected) {
        window.location.href = response.url;
        return;
      }

      const data = await response.json();

      // Check if there's a redirect in the response
      if (data.redirect_browser_to) {
        window.location.href = data.redirect_browser_to;
        return;
      }

      return data;
    } catch (error: any) {
      logger.error("OAuth submission failed:", error);
      throw error;
    }
  };

  /**
   * Get OAuth URL for opening in popup (doesn't initiate redirect)
   */
  const getOAuthUrl = async (
    flowId: string,
    provider: string,
    csrfToken: string,
    flowType: "login" | "registration",
    upstreamParams?: Record<string, string>,
  ): Promise<string> => {
    try {
      const endpoint =
        flowType === "login"
          ? `/self-service/login?flow=${flowId}`
          : `/self-service/registration?flow=${flowId}`;

      // Build the body with upstream parameters
      const body: any = {
        csrf_token: csrfToken,
        provider: provider,
      };

      if (upstreamParams) {
        Object.entries(upstreamParams).forEach(([key, value]) => {
          body[`upstream_parameters.${key}`] = value;
        });
      }

      const response = await fetch(`${kratosUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
        redirect: "manual", // Don't follow redirects
      });

      const data = await response.json();

      // Return the redirect URL if available
      if (data.redirect_browser_to) {
        return data.redirect_browser_to;
      }

      throw new Error("No redirect URL returned from Kratos");
    } catch (error: any) {
      logger.error("Failed to get OAuth URL:", error);
      throw error;
    }
  };

  /**
   * Submit OAuth flow in a popup window (preserves page state)
   */
  const submitOAuthFlowPopup = async (
    flowId: string,
    provider: string,
    csrfToken: string,
    flowType: "login" | "registration",
    upstreamParams?: Record<string, string>,
  ): Promise<{ success: boolean; session?: any }> => {
    // Open popup FIRST, synchronously, to avoid popup blockers
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Open with a loading page first
    const popup = window.open(
      "about:blank",
      "oauth_popup",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
    );

    if (!popup) {
      throw new Error("Popup blocked - please allow popups for this site");
    }

    // Show loading message in popup
    popup.document.write(`
      <html>
        <head>
          <title>Connexion en cours...</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              background: #f5f5f5;
            }
            .loader {
              text-align: center;
            }
            .spinner {
              border: 3px solid #f3f3f3;
              border-top: 3px solid #D0A444;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="loader">
            <div class="spinner"></div>
            <p>Connexion avec Google...</p>
          </div>
        </body>
      </html>
    `);

    try {
      const endpoint =
        flowType === "login"
          ? `/self-service/login?flow=${flowId}`
          : `/self-service/registration?flow=${flowId}`;

      // Build the body with upstream parameters if provided
      const body: any = {
        csrf_token: csrfToken,
        provider: provider,
      };

      // Add upstream parameters with the correct prefix
      if (upstreamParams) {
        Object.entries(upstreamParams).forEach(([key, value]) => {
          body[`upstream_parameters.${key}`] = value;
        });
      }

      const response = await fetch(`${kratosUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });

      const data = await response.json();

      // Get redirect URL (either from redirected response or data)
      const redirectUrl = response.redirected
        ? response.url
        : data.redirect_browser_to;

      if (!redirectUrl) {
        popup.close();
        throw new Error("No redirect URL received from Kratos");
      }

      // Navigate popup to OAuth provider
      popup.location.href = redirectUrl;

      // Wait for popup to complete OAuth flow
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          try {
            // Check if popup was closed
            if (popup.closed) {
              clearInterval(checkInterval);
              window.removeEventListener("message", messageHandler);
              reject(new Error("OAuth popup was closed"));
            }
          } catch (e) {
            // Ignore cross-origin errors while waiting
          }
        }, 500);

        // Listen for message from auth-success page
        const messageHandler = (event: MessageEvent) => {
          // Security: verify origin
          if (event.origin !== window.location.origin) return;

          if (event.data?.type === "oauth_success") {
            clearInterval(checkInterval);
            window.removeEventListener("message", messageHandler);
            popup.close();
            resolve({ success: true, session: event.data.session });
          } else if (event.data?.type === "oauth_error") {
            clearInterval(checkInterval);
            window.removeEventListener("message", messageHandler);
            popup.close();
            reject(new Error(event.data.error || "OAuth failed"));
          }
        };

        window.addEventListener("message", messageHandler);
      });
    } catch (error: any) {
      popup.close();
      logger.error("OAuth popup submission failed:", error);
      throw error;
    }
  };

  /**
   * Get Google OAuth URL from login flow
   */
  const getGoogleOAuthUrl = (
    flow: LoginFlow | RegistrationFlow,
  ): string | null => {
    const googleNode = flow.ui.nodes.find(
      (node: any) =>
        node.attributes?.name === "provider" &&
        node.attributes?.value === "google" &&
        node.group === "oidc",
    );

    // For anchor nodes (type "a"), the href is in attributes
    if (
      googleNode &&
      googleNode.attributes &&
      "href" in googleNode.attributes
    ) {
      return (googleNode.attributes as any).href;
    }

    return null;
  };

  /**
   * Extract form nodes from flow UI
   */
  const getFormNodes = (nodes: any[]) => {
    return nodes.filter(
      (node) =>
        node.group === "default" ||
        node.group === "password" ||
        node.group === "profile",
    );
  };

  /**
   * Translate Kratos error messages to French
   */
  const translateError = (text: string): string => {
    const translations: Record<string, string> = {
      // Password validation errors
      "The password can not be used because the password has been found in data breaches and must no longer be used.":
        "Ce mot de passe a été trouvé dans des violations de données et ne peut plus être utilisé.",
      "The password can not be used because it is too similar to the identifier.":
        "Le mot de passe ne peut pas être utilisé car il est trop similaire à l'identifiant.",
      "The password can not be used because it is too similar to the user data.":
        "Le mot de passe ne peut pas être utilisé car il est trop similaire à vos données personnelles.",
      "The password does not fulfill the password policy because the password is too short.":
        "Le mot de passe ne respecte pas la politique de sécurité car il est trop court.",
      "The password does not fulfill the password policy because the password is too long.":
        "Le mot de passe ne respecte pas la politique de sécurité car il est trop long.",
      "The password does not fulfill the password policy because it is too similar.":
        "Le mot de passe ne respecte pas la politique de sécurité car il est trop similaire.",
      "The password does not fulfill the password policy.":
        "Le mot de passe ne respecte pas la politique de sécurité.",
      "The new password must be different from the old password.":
        "Le nouveau mot de passe doit être différent de l'ancien mot de passe.",
      "password must be at least 12 characters long":
        "Le mot de passe doit contenir au moins 12 caractères",
      "The password must be at least 12 characters long":
        "Le mot de passe doit contenir au moins 12 caractères",

      // Recovery/verification errors
      "The recovery code is invalid or has already been used. Please try again.":
        "Le code de récupération est invalide ou a déjà été utilisé. Veuillez réessayer.",
      "The verification code is invalid or has already been used. Please try again.":
        "Le code de vérification est invalide ou a déjà été utilisé. Veuillez réessayer.",
      "The recovery flow expired": "La session de récupération a expiré",
      "The verification flow expired": "La session de vérification a expiré",
      "Could not find a strategy to recover your account with. Did you fill out the form correctly?":
        "Impossible de trouver une méthode pour récupérer votre compte. Avez-vous rempli le formulaire correctement ?",
      "The recovery token is invalid or has already been used. Please try again.":
        "Le jeton de récupération est invalide ou a déjà été utilisé. Veuillez réessayer.",

      // Email errors
      "An account with this email address does not exist.":
        "Aucun compte avec cette adresse email n'existe.",
      "An account with this email address already exists.":
        "Un compte avec cette adresse email existe déjà.",
      "This email address is not valid.":
        "Cette adresse email n'est pas valide.",
      "The email address is not valid.": "L'adresse email n'est pas valide.",
      "Property email is missing.": "L'adresse email est requise.",

      // Login errors
      "The provided credentials are invalid, check for spelling mistakes in your password or username, email address, or phone number.":
        "Les identifiants fournis sont invalides. Vérifiez l'orthographe de votre mot de passe, nom d'utilisateur, email ou numéro de téléphone.",
      "The password can not be used because it is a breached password.":
        "Ce mot de passe ne peut pas être utilisé car il a été compromis dans une fuite de données.",
      "The account is not active.": "Le compte n'est pas actif.",

      // CSRF errors
      "A security violation was detected, please fill out the form again.":
        "Une violation de sécurité a été détectée, veuillez remplir le formulaire à nouveau.",
      "The CSRF token is invalid.": "Le jeton de sécurité est invalide.",

      // General errors
      "The request was malformed.": "La requête était mal formée.",
      "An internal server error occurred.":
        "Une erreur serveur interne s'est produite.",
      "The flow has expired. Please try again.":
        "La session a expiré. Veuillez réessayer.",
      "Unable to complete request, please try again.":
        "Impossible de terminer la requête, veuillez réessayer.",
    };

    // Check for exact match
    if (translations[text]) {
      return translations[text];
    }

    // Check for partial matches (case-insensitive)
    const lowerText = text.toLowerCase();
    for (const [key, value] of Object.entries(translations)) {
      if (lowerText.includes(key.toLowerCase())) {
        return value;
      }
    }

    // If no translation found, return original text
    return text;
  };

  /**
   * Extract error messages from flow UI
   */
  const getFlowErrors = (flow: any): string[] => {
    const errors: string[] = [];

    // Check if flow and flow.ui exist
    if (!flow || !flow.ui) {
      return errors;
    }

    // Global messages
    if (flow.ui.messages) {
      flow.ui.messages.forEach((msg: any) => {
        if (msg.type === "error") {
          errors.push(translateError(msg.text));
        }
      });
    }

    // Node-specific messages
    flow.ui.nodes.forEach((node: any) => {
      if (node.messages) {
        node.messages.forEach((msg: any) => {
          if (msg.type === "error") {
            errors.push(translateError(msg.text));
          }
        });
      }
    });

    return errors;
  };

  /**
   * Initialize settings flow (for password change, profile update, etc.)
   */
  const initSettingsFlow = async () => {
    try {
      const { data: flow } = await kratos.createBrowserSettingsFlow();
      return flow;
    } catch (error) {
      logger.error("Failed to initialize settings flow:", error);
      throw error;
    }
  };

  /**
   * Get existing settings flow
   */
  const getSettingsFlow = async (flowId: string) => {
    try {
      const { data: flow } = await kratos.getSettingsFlow({ id: flowId });
      return flow;
    } catch (error) {
      logger.error("Failed to get settings flow:", error);
      throw error;
    }
  };

  /**
   * Update settings flow (change password, update profile, etc.)
   */
  const updateSettingsFlow = async (flowId: string, values: any) => {
    try {
      const { data } = await kratos.updateSettingsFlow({
        flow: flowId,
        updateSettingsFlowBody: values,
      });
      return data;
    } catch (error: any) {
      logger.error("Settings update failed:", error);
      throw error;
    }
  };

  /**
   * Submit password in recovery flow (final step after code validation)
   */
  const submitRecoveryPasswordFlow = async (
    flowId: string,
    password: string,
    csrfToken: string,
  ) => {
    try {
      const { data } = await kratos.updateRecoveryFlow({
        flow: flowId,
        updateRecoveryFlowBody: {
          method: "code",
          csrf_token: csrfToken,
        },
      });
      return data;
    } catch (error: any) {
      logger.error("Recovery password submission failed:", error);
      throw error;
    }
  };

  return {
    // Flow initialization
    initLoginFlow,
    initRegistrationFlow,
    initRecoveryFlow,
    initVerificationFlow,
    initSettingsFlow,
    getVerificationFlow,
    getRecoveryFlow,
    getSettingsFlow,

    // Flow submission
    submitLoginFlow,
    submitRegistrationFlow,
    submitRecoveryFlow,
    submitRecoveryCodeFlow,
    submitRecoveryPasswordFlow,
    submitVerificationFlow,
    requestVerificationEmail,
    submitOAuthFlow,
    submitOAuthFlowPopup,
    getOAuthUrl,
    updateSettingsFlow,

    // Session management
    getSession,
    logout,

    // Helper methods
    getGoogleOAuthUrl,
    getFormNodes,
    getFlowErrors,
  };
};
